/**
 * Timezone utility functions for converting times between timezones
 */

import { format, parseISO } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * Convert a time from one timezone to another, accounting for DST
 *
 * @param timeString    Time in HH:MM:SS format (e.g., "09:00:00")
 * @param fromTimezone  Source timezone (e.g., "Australia/Sydney")
 * @param toTimezone    Target timezone (e.g., "America/Chicago")
 * @param referenceDate Optional reference date for DST accuracy (YYYY-MM-DD format)
 * @return Converted time in HH:MM:SS format
 */
export function convertTimeToTimezone(
	timeString: string,
	fromTimezone: string,
	toTimezone: string,
	referenceDate?: string | null
): string {
	// If timezones are the same, no conversion needed
	if (fromTimezone === toTimezone) {
		return timeString;
	}

	// Use reference date if provided, otherwise use current date
	const baseDate = referenceDate || new Date().toISOString().split('T')[0];

	// Create datetime string
	const sourceDateTime = parseISO(`${baseDate}T${timeString}`);

	// Convert using date-fns-tz
	// This treats the sourceDateTime as being in the fromTimezone
	// and converts it to what that same "wall clock" time would be in toTimezone
	const convertedDateTime = toZonedTime(
		fromZonedTime(sourceDateTime, fromTimezone),
		toTimezone
	);

	// Return formatted time
	return format(convertedDateTime, 'HH:mm:ss');
}

/**
 * Determine the appropriate reference date for timezone calculations
 * Uses the availability block's start date if available, otherwise current date
 *
 * @param startDate Optional start date (YYYY-MM-DD format)
 * @param endDate   Optional end date (YYYY-MM-DD format)
 * @return Reference date in YYYY-MM-DD format
 */
export function getTimezoneReferenceDate(
	startDate?: string | null,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Saving for future use
	endDate?: string | null
): string {
	if (startDate) {
		const start = new Date(startDate);
		const now = new Date();

		// Use the later of start date or current date for DST accuracy
		const referenceDate = start > now ? start : now;
		return referenceDate.toISOString().split('T')[0];
	}

	// No start date, use current date
	return new Date().toISOString().split('T')[0];
}

/**
 * Convert a time range from one timezone to another
 *
 * @param startTime     Start time in HH:MM:SS format
 * @param endTime       End time in HH:MM:SS format
 * @param fromTimezone  Source timezone
 * @param toTimezone    Target timezone
 * @param referenceDate Optional reference date for DST accuracy
 * @return Object with converted start and end times
 */
export function convertTimeRangeToTimezone(
	startTime: string,
	endTime: string,
	fromTimezone: string,
	toTimezone: string,
	referenceDate?: string | null
): { startTime: string; endTime: string } {
	return {
		startTime: convertTimeToTimezone(
			startTime,
			fromTimezone,
			toTimezone,
			referenceDate
		),
		endTime: convertTimeToTimezone(
			endTime,
			fromTimezone,
			toTimezone,
			referenceDate
		),
	};
}
